// src/components/UserProfilePage/Bookings/Bookings.jsx
/**
 *
 *  User Profile Page
 *  Bookings Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { Link } from "react-router-dom";

import { useAppState } from "../../../state";
import { useRegion } from "../../../context/RegionProvider";

// Components
import { WorkoutIcon } from "../../PartnerProductBadge/PartnerProductBadge";
import SessionLink from "../../SessionLink/SessionLink";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import TimeZoneLabel from "../../TimeZoneLabel/TimeZoneLabel";
import { Skeleton } from "../../Skeleton/Skeleton";
import Button from "../../Button/Button";

// Icons
import SessionTypeIcon from "../SessionTypeIcon";
import CalendarIcon from "../../../icons/CalendarTruBeIcon";
import CaretDownIcon from "../../../icons/CaretDownIcon";
import MapMarkerIcon from "../../../icons/MapMarkerIcon";
import SessionTypeIconGreyedOut from "../../../icons/SessionTypeIconGreyedOut";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

// Utils
import { getPartnerPictureUrl } from "../../../utils";
import { uniqueId } from "../../../utils/_";
import { getTz } from "../../../utils/date";
import { PAGE_SIZE } from "../../../constants";

// Images
import defaultPartnerPhoto from "../../../images/generic-trainerphoto.svg";

// Hooks
import {
  useGetJoinedOnlyEvents,
  useGetUpcomingBookings,
  useGetPastBookings,
} from "../../../hooks/useEvents/useEvents";

const TABS = {
  ONE_ON_ONE: "1:1 Session",
  GROUP_CLASSES: "Group Classes",
};

const BookingTile = ({
  className,
  trainerFirstName,
  trainerFamilyName,
  isCancelled,
  isPastBooking,
  productLabel,
  trainerPictureUrl,
  bookingId,
  bookingStartTimePart,
  bookingStartTimeAmPmPart,
  bookingStartTimeWeekdayPart,
  bookingStartTimeSecondPart,
  bookingStartTimeShort,
  onClick,
  type = "1:1", // '1:1' или 'group'
}) => {
  const [_id] = useState(`booking_tile_icon_${uniqueId()}`);

  return (
    <Link
      to={`/account/bookings/${bookingId}`}
      className={`${className} tbk-relative tbk-flex tbk-min-h-[120px] tbk-cursor-pointer tbk-flex-row-reverse tbk-items-stretch tbk-overflow-hidden tbk-rounded-2xl tbk-shadow-trube md:tbk-min-h-[96px] md:tbk-flex-row`}
      onClick={onClick}
    >
      <div className="tb-profile-picture md:tbk-grow-1 tbk-relative tbk-max-h-[160px] tbk-shrink-0 tbk-basis-28 md:tbk-mr-2 md:tbk-max-h-[96px] md:tbk-basis-24">
        <img
          className="tb-profile-picture-img tbk-absolute tbk-h-full tbk-w-full tbk-rounded-2xl tbk-object-cover"
          itemProp="image"
          src={trainerPictureUrl || defaultPartnerPhoto}
          alt="Coach profile"
        />
      </div>
      {isCancelled ? (
        <div className="tbk-text-small tbk-absolute tbk-bottom-0 tbk-left-0 tbk-rounded-lg tbk-bg-blue-grey tbk-px-1 tbk-py-0.5 tbk-text-basic-white">
          Cancelled
        </div>
      ) : null}

      <div className="tbk-flex tbk-min-w-0 tbk-grow tbk-flex-col tbk-justify-start tbk-py-2 md:tbk-grid md:tbk-grid-cols-account-booking-tile">
        <div className="tbk-order-3 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light">
          <div className="tbk-text-small md:tbk-text-h3-subtitle tbk-inline-block tbk-text-blue-grey md:tbk-mb-1 md:tbk-block md:tbk-text-primary">
            {trainerFirstName}&nbsp;
          </div>
          <div className="tbk-text-small md:tbk-text-h3-subtitle tbk-inline-block tbk-text-blue-grey md:tbk-block md:tbk-text-primary">
            {trainerFamilyName}
          </div>
        </div>
        <div className="tbk-order-1 tbk-mb-0.5 tbk-flex tbk-grow-0 tbk-items-center md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2">
          <div className="tbk-flex tbk-min-w-0 tbk-items-start">
            <div className="tbk-mr-1 tbk-hidden md:tbk-block">
              <CalendarIcon
                size={32}
                className={
                  isPastBooking ? "tbk-text-blue-grey" : "tbk-text-green"
                }
              />
            </div>
            <div className="tbk-text-main tbk-min-w-0 tbk-text-primary">
              <div className="tbk-hidden tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap md:tbk-block">
                {bookingStartTimePart}
                <span className="tbk-text-small !tbk-leading-none">
                  {bookingStartTimeAmPmPart}
                </span>{" "}
                {bookingStartTimeWeekdayPart},
              </div>
              <div className="tbk-hidden tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap md:tbk-block">
                {" "}
                {bookingStartTimeSecondPart}
              </div>
              <div className="md:tbk-hidden">{bookingStartTimeShort}</div>
            </div>
          </div>
        </div>
        <div className="tbk-order-2 tbk-flex tbk-min-w-0 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2 xl:tbk-px-5">
          <div className="tbk-max-w-full">
            <div className="tbk-flex tbk-items-center">
              <div className="tbk-hidden md:tbk-block">
                <WorkoutIcon label={productLabel} size={32} />
              </div>
              <div className="tbk-text-secondary md:tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary md:tbk-mb-0">
                {productLabel}
                {type === "1:1" && <span className="md:tbk-hidden"> with</span>}
              </div>
            </div>
            <div className="tbk-hidden tbk-items-center md:tbk-flex">
              <div className="tbk-mr-0.5 tbk-text-green">
                <MapMarkerIcon size={32} />
              </div>
              <div className="tbk-text-main tbk-text-primary">In-Person</div>
            </div>
          </div>
        </div>
        <div className="tbk-hidden tbk-grow tbk-items-center tbk-justify-center md:tbk-flex">
          <SessionLink to={`/account/bookings/${bookingId}`} color="">
            View details
          </SessionLink>
        </div>
      </div>
      <div className="tbk-shrink-0 tbk-self-stretch tbk-p-2 md:tbk-hidden">
        {isPastBooking || isCancelled ? (
          <SessionTypeIconGreyedOut className="tbk-mr-1 tbk-text-blue-grey" />
        ) : (
          <SessionTypeIcon
            id={_id}
            className="tbk-mr-1 tbk-drop-shadow-trube"
          />
        )}
      </div>
    </Link>
  );
};

const EventTile = ({ className, event, isPastEvent, onClick }) => {
  const { region } = useRegion();

  const mainPartner = event.partners?.[0];
  const eventDate = moment.utc(event.start).tz(getTz(region));

  return (
    <Link
      to={`/events/${event.id}`}
      target="_blank"
      className={`${className} tbk-relative tbk-flex tbk-min-h-[120px] tbk-cursor-pointer tbk-flex-row-reverse tbk-items-stretch tbk-overflow-hidden tbk-rounded-2xl tbk-shadow-trube md:tbk-min-h-[96px] md:tbk-flex-row`}
      onClick={onClick}
    >
      <div className="tb-profile-picture md:tbk-grow-1 tbk-relative tbk-max-h-[160px] tbk-shrink-0 tbk-basis-28 md:tbk-mr-2 md:tbk-max-h-[96px] md:tbk-basis-24">
        <img
          className="tb-profile-picture-img tbk-absolute tbk-h-full tbk-w-full tbk-rounded-2xl tbk-object-cover"
          src={event.pastPhoto || defaultPartnerPhoto}
          alt="Event cover"
        />
      </div>

      <div className="tbk-flex tbk-min-w-0 tbk-grow tbk-flex-col tbk-justify-start tbk-py-2 tbk-pl-2 md:tbk-grid md:tbk-grid-cols-account-booking-tile lg:tbk-pl-0">
        <div className="lg:tbk-order-3_ tbk-order-2 tbk-mb-0.5 tbk-min-h-0 tbk-grow-0 md:tbk-order-none md:tbk-mb-0 md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light lg:tbk-self-center">
          <div className="tbk-text-secondary _md:tbk-text-h3-subtitle tbk-truncate_ md:tbk-text-blue-grey_ tbk-inline-block tbk-max-w-full tbk-text-primary md:tbk-block">
            {event.name}
          </div>
          {/* {mainPartner && (
            <div className="tbk-mt-0.5 md:tbk-mt-1 tbk-text-small tbk-text-blue-grey md:tbk-block">
              with {mainPartner.firstName} {mainPartner.lastName}
            </div>
          )} */}
        </div>
        <div className="tbk-order-1 tbk-mb-0.5 tbk-flex tbk-grow-0 tbk-items-center md:tbk-order-none md:tbk-mb-0 md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2">
          <div className="tbk-flex tbk-min-w-0 tbk-items-start">
            <div className="tbk-mr-1 tbk-hidden md:tbk-block">
              <CalendarIcon
                size={32}
                className={
                  isPastEvent ? "tbk-text-blue-grey" : "tbk-text-green"
                }
              />
            </div>
            <div className="tbk-text-main tbk-min-w-0 tbk-text-primary">
              <div className="tbk-hidden tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap md:tbk-block">
                {eventDate.format("h:mm")}
                <span className="tbk-text-small !tbk-leading-none">
                  {eventDate.format("A")}
                </span>{" "}
                {eventDate.format("dddd")},
              </div>
              <div className="tbk-hidden tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap md:tbk-block">
                {eventDate.format("MMMM D, YYYY")}
              </div>
              <div className="md:tbk-hidden">
                {eventDate.format("h:mma dddd, MMM D")}
              </div>
            </div>
          </div>
        </div>
        <div className="2 tbk-order-3 tbk-flex tbk-min-h-0 tbk-min-w-0 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2 lg:tbk-order-none xl:tbk-px-5">
          <div className="tbk-flex tbk-max-w-full tbk-items-center">
            <div className="tbk-mr-0.5 tbk-hidden tbk-text-green md:tbk-block">
              <MapMarkerIcon size={24} />
            </div>
            <div className="tbk-text-small md:tbk-text-secondary tbk-max-w-full tbk-truncate tbk-text-blue-grey lg:tbk-text-primary">
              {event.location?.name || ""}
            </div>
          </div>
        </div>
        <div className="tbk-hidden tbk-grow tbk-items-center tbk-justify-center md:tbk-flex">
          <SessionLink to={`/events/${event.id}`} color="">
            View details
          </SessionLink>
        </div>
      </div>
      {/* <div className="tbk-shrink-0 tbk-self-stretch tbk-p-2 md:tbk-hidden">
        {isPastEvent ? (
          <SessionTypeIconGreyedOut className="tbk-mr-1 tbk-text-blue-grey" />
        ) : (
          <SessionTypeIcon
            id={`event_tile_icon_${event.id}`}
            className="tbk-mr-1 tbk-drop-shadow-trube"
          />
        )}
      </div> */}
    </Link>
  );
};

// Скелетон для BookingTile
const BookingTileSkeleton = ({ className }) => (
  <div
    className={`${className} tbk-relative tbk-flex tbk-min-h-[120px] tbk-flex-row-reverse tbk-items-stretch tbk-overflow-hidden tbk-rounded-2xl tbk-shadow-trube md:tbk-min-h-[96px] md:tbk-flex-row`}
  >
    <div className="tb-profile-picture md:tbk-grow-1 tbk-relative tbk-max-h-[160px] tbk-shrink-0 tbk-basis-28 md:tbk-mr-2 md:tbk-max-h-[96px] md:tbk-basis-24">
      <Skeleton className="tbk-absolute tbk-h-full tbk-w-full tbk-rounded-2xl" />
    </div>

    <div className="tbk-flex tbk-min-w-0 tbk-grow tbk-flex-col tbk-justify-start tbk-py-2 md:tbk-grid md:tbk-grid-cols-account-booking-tile lg:tbk-justify-center">
      <div className="tbk-order-3 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light">
        <Skeleton className="tbk-mb-1 tbk-h-4 tbk-w-24" />
        <Skeleton className="tbk-w-20 tbk-mr-2 tbk-h-4" />
      </div>
      <div className="tbk-order-1 tbk-mb-0.5 tbk-flex tbk-grow-0 tbk-items-center md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2">
        <div className="tbk-flex tbk-min-w-0 tbk-items-start">
          <div className="tbk-mr-1 tbk-hidden md:tbk-block">
            <Skeleton className="tbk-h-4 tbk-w-4 tbk-rounded" />
          </div>
          <div className="tbk-min-w-0">
            <Skeleton className="tbk-mb-1 tbk-hidden tbk-h-3 tbk-w-32 md:tbk-block" />
            <Skeleton className="tbk-hidden tbk-h-3 tbk-w-28 md:tbk-block" />
            <Skeleton className="tbk-h-3 tbk-w-36 md:tbk-hidden" />
          </div>
        </div>
      </div>
      <div className="tbk-order-2 tbk-flex tbk-min-w-0 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2 xl:tbk-px-5">
        <div className="tbk-mb-0.5 tbk-max-w-full">
          <div className="tbk-flex tbk-min-w-0 tbk-items-start">
            <div className="tbk-mr-1 tbk-hidden md:tbk-block">
              <Skeleton className="tbk-h-4 tbk-w-4 tbk-rounded" />
            </div>
            <div className="tbk-min-w-0">
              <Skeleton className="tbk-mb-1 tbk-hidden tbk-h-3 tbk-w-32 md:tbk-block" />
              <Skeleton className="tbk-hidden tbk-h-3 tbk-w-28 md:tbk-block" />
              <Skeleton className="tbk-h-3 tbk-w-36 md:tbk-hidden" />
            </div>
          </div>
        </div>
      </div>
      <div className="tbk-hidden tbk-grow tbk-items-center tbk-justify-center md:tbk-flex">
        <Skeleton className="tbk-h-4 tbk-w-32 tbk-rounded lg:tbk-w-40" />
      </div>
    </div>
    <div className="tbk-shrink-0 tbk-self-stretch tbk-p-2 md:tbk-hidden">
      <Skeleton className="tbk-h-6 tbk-w-6 tbk-rounded" />
    </div>
  </div>
);

// Скелетон для EventTile
const EventTileSkeleton = ({ className }) => (
  <div
    className={`${className} tbk-relative tbk-flex tbk-min-h-[120px] tbk-flex-row-reverse tbk-items-stretch tbk-overflow-hidden tbk-rounded-2xl tbk-shadow-trube md:tbk-min-h-[96px] md:tbk-flex-row`}
  >
    <div className="tb-profile-picture md:tbk-grow-1 tbk-relative tbk-max-h-[160px] tbk-shrink-0 tbk-basis-28 md:tbk-mr-2 md:tbk-max-h-[96px] md:tbk-basis-24">
      <Skeleton className="tbk-absolute tbk-h-full tbk-w-full tbk-rounded-2xl" />
    </div>

    <div className="tbk-flex tbk-min-w-0 tbk-grow tbk-flex-col tbk-justify-start tbk-py-2 tbk-pl-2 md:tbk-grid md:tbk-grid-cols-account-booking-tile md:tbk-pl-0">
      <div className=" tbk-order-3 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light lg:tbk-self-center">
        <Skeleton className="tbk-mb-1_ tbk-h-4 tbk-w-32" />
        {/* <Skeleton className="tbk-h-3 tbk-w-24" /> */}
      </div>
      <div className="tbk-order-1 tbk-mb-0.5 tbk-flex tbk-grow-0 tbk-items-center md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2">
        <div className="tbk-flex tbk-min-w-0 tbk-items-start">
          <div className="tbk-mr-1 tbk-hidden md:tbk-block">
            <Skeleton className="tbk-h-4 tbk-w-4 tbk-rounded" />
          </div>
          <div className="tbk-min-w-0">
            <Skeleton className="tbk-mb-1 tbk-hidden tbk-h-3 tbk-w-32 md:tbk-block" />
            <Skeleton className="tbk-hidden tbk-h-3 tbk-w-28 md:tbk-block" />
            <Skeleton className="tbk-h-3 tbk-w-36 md:tbk-hidden" />
          </div>
        </div>
      </div>
      <div className="tbk-order-2 tbk-flex tbk-min-w-0 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2 xl:tbk-px-5">
        <div className="tbk-mb-0.5 tbk-max-w-full">
          <div className="tbk-flex tbk-min-w-0 tbk-items-start">
            {/* <div className="tbk-mr-1 tbk-hidden md:tbk-block">
              <Skeleton className="tbk-h-4 tbk-w-4 tbk-rounded" />
            </div> */}
            <div className="tbk-min-w-0">
              <Skeleton className="tbk-mb-1 tbk-hidden tbk-h-3 tbk-w-32 md:tbk-block" />
              <Skeleton className="tbk-hidden tbk-h-3 tbk-w-28 md:tbk-block" />
              <Skeleton className="tbk-h-3 tbk-w-36 md:tbk-hidden" />
            </div>
          </div>
        </div>
      </div>
      <div className="tbk-hidden tbk-grow tbk-items-center tbk-justify-center md:tbk-flex">
        <Skeleton className="tbk-h-4 tbk-w-32 tbk-rounded lg:tbk-w-40" />
      </div>
    </div>
    {/* <div className="tbk-shrink-0 tbk-self-stretch tbk-p-2 md:tbk-hidden">
      <Skeleton className="tbk-h-6 tbk-w-6 tbk-rounded" />
    </div> */}
  </div>
);

const TabButton = ({ className, active, onClick, children }) => (
  <button
    className={`tbk-text-secondary md:tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-flex-1 tbk-border-b-4 tbk-bg-basic-white tbk-px-4 tbk-py-2 md:tbk-w-76 md:tbk-flex-none ${
      active
        ? "tbk-border-b-coral-light tbk-text-primary"
        : "tbk-border-blue-grey-light tbk-text-blue-grey hover:tbk-border-b-coral-light hover:tbk-text-primary"
    } ${className}`}
    onClick={onClick}
  >
    {children}
  </button>
);

export const Bookings = () => {
  const history = useHistory();
  const { region } = useRegion();
  const [activeTab, setActiveTab] = useState(TABS.GROUP_CLASSES);
  const { user } = useAppState();

  // Хуки для upcoming групповых классов
  const {
    events: upcomingEvents,
    isPending: upcomingEventsLoading,
    hasNextPage: hasMoreUpcomingEvents,
    fetchNextPage: loadMoreUpcomingEvents,
    isFetchingNextPage: isFetchingMoreUpcomingEvents,
  } = useGetJoinedOnlyEvents({
    customerId: user?.id,
    region: user?.region || region,
    size: PAGE_SIZE,
    sort: "start,asc", // Для upcoming сортируем по возрастанию
    status: ["UPCOMING", "UPCOMING_FULL", "STARTED"], // Только будущие события
    enabled: activeTab === TABS.GROUP_CLASSES && !!user?.id,
  });

  // Хуки для past групповых классов
  const {
    events: pastEvents,
    isPending: pastEventsLoading,
    hasNextPage: hasMorePastEvents,
    fetchNextPage: loadMorePastEvents,
    isFetchingNextPage: isFetchingMorePastEvents,
  } = useGetJoinedOnlyEvents({
    customerId: user?.id,
    region: user?.region || region,
    size: PAGE_SIZE,
    sort: "start,desc", // Для past сортируем по убыванию
    status: ["COMPLETED"], // Только завершенные события
    enabled: activeTab === TABS.GROUP_CLASSES && !!user?.id,
  });

  // Хуки для 1:1 сессий (остаются без изменений)
  const {
    bookings: upcomingBookings,
    isPending: upcomingLoading,
    hasNextPage: hasMoreUpcoming,
    fetchNextPage: loadMoreUpcoming,
    isFetchingNextPage: isFetchingMoreUpcoming,
  } = useGetUpcomingBookings({
    customerId: user?.id,
    region: user?.region || region,
    size: PAGE_SIZE,
    enabled: activeTab === TABS.ONE_ON_ONE && !!user?.id,
  });

  const {
    bookings: pastBookings,
    isPending: pastLoading,
    hasNextPage: hasMorePast,
    fetchNextPage: loadMorePast,
    isFetchingNextPage: isFetchingMorePast,
  } = useGetPastBookings({
    customerId: user?.id,
    region: user?.region || region,
    size: PAGE_SIZE,
    enabled: activeTab === TABS.ONE_ON_ONE && !!user?.id,
  });

  // Функция для рендеринга скелетонов
  const renderSkeletons = (count = 3, type = "booking") => {
    const skeletons = [];
    for (let i = 0; i < count; i++) {
      skeletons.push(
        type === "booking" ? (
          <BookingTileSkeleton
            key={`skeleton-booking-${i}`}
            className="tbk-mb-2 md:tbk-max-h-[96px]"
          />
        ) : (
          <EventTileSkeleton
            key={`skeleton-event-${i}`}
            className="tbk-mb-2 md:tbk-max-h-[96px]"
          />
        )
      );
    }
    return skeletons;
  };

  const renderOneOnOneTab = () => (
    <>
      <div className="tbk-mb-5">
        <div className="tbk-text-h3-subtitle tbk-mb-2 tbk-mt-4 tbk-text-blue-grey md:tbk-mb-2 md:tbk-mt-4">
          Upcoming:
        </div>
        <div className="tbk-text-small tbk-mb-3 tbk-hidden tbk-text-blue-grey md:tbk-mb-4">
          Time set to{" "}
          <TimeZoneLabel
            region={user ? user.region : process.env.REACT_APP_REGION}
          />
        </div>
        {upcomingLoading ? (
          renderSkeletons(2, "booking")
        ) : upcomingBookings && upcomingBookings.length ? (
          upcomingBookings.map((booking) => (
            <BookingTile
              key={booking.id}
              className="tbk-mb-2 md:tbk-max-h-[96px]"
              trainerPictureUrl={getPartnerPictureUrl(booking.partner)}
              trainerFirstName={booking.partner.firstName}
              trainerFamilyName={booking.partner.lastName}
              productLabel={booking.product.name}
              bookingId={booking.id}
              bookingStartTimePart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("h:mm")}
              bookingStartTimeAmPmPart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("A")}
              bookingStartTimeWeekdayPart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("dddd")}
              bookingStartTimeSecondPart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("MMMM D, YYYY")}
              bookingStartTimeShort={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("h:mma dddd, MMM D")}
              type="1:1"
            />
          ))
        ) : (
          <div className="tbk-text-main tbk-mb-7 tbk-mt-7 tbk-text-center tbk-text-blue-grey">
            <div className="tbk-mb-4">You have no upcoming sessions</div>
            <Link className="tbk-inline-block" to={`/partners`}>
              <Button
                variant="secondary"
                className="tbk-pl-5 tbk-pr-5 lg:tbk-w-66"
                endIcon={<ArrowRightIcon />}
              >
                View Coaches
              </Button>
            </Link>
          </div>
        )}
      </div>

      {hasMoreUpcoming && !isFetchingMoreUpcoming && (
        <div className="tbk-relative tbk-mb-7 tbk-mt-5 tbk-text-center">
          <button
            className="tbk-inline-block tbk-bg-grey-light tbk-px-[19px] tbk-py-0"
            onClick={() => loadMoreUpcoming()}
          >
            <div className="tbk-flex tbk-text-blue">
              Show More Upcoming
              <CaretDownIcon />
            </div>
          </button>
          <hr className="tbk-mx-auto tbk--mt-1.5 tbk-mb-0 tbk-border-blue-grey-light" />
        </div>
      )}

      {isFetchingMoreUpcoming && (
        <div className="tbk-mb-5">{renderSkeletons(1, "booking")}</div>
      )}

      <hr />
      <div>
        <div className="tbk-text-h3-subtitle tbk-mb-2 tbk-mt-5 tbk-text-blue-grey">
          Past:
        </div>
        {pastLoading ? (
          renderSkeletons(3, "booking")
        ) : pastBookings && pastBookings.length ? (
          pastBookings.map((booking) => (
            <BookingTile
              key={booking.id}
              className="tbk-mb-2 md:tbk-max-h-[96px]"
              trainerPictureUrl={getPartnerPictureUrl(booking.partner)}
              trainerFirstName={booking.partner.firstName}
              trainerFamilyName={booking.partner.lastName}
              productLabel={booking.product.name}
              isCancelled={booking.status === "CANCELLED"}
              isPastBooking={true}
              bookingId={booking.id}
              bookingStartTimePart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("h:mm")}
              bookingStartTimeAmPmPart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("A")}
              bookingStartTimeWeekdayPart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("dddd")}
              bookingStartTimeSecondPart={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("MMMM D, YYYY")}
              bookingStartTimeShort={moment
                .utc(booking.start)
                .tz(getTz(region))
                .format("h:mma dddd, MMM D")}
              // onClick={() => history.push(`/account/bookings/${booking.id}`)}
              type="1:1"
            />
          ))
        ) : (
          <div className="tbk-text-main tbk-mt-1 tbk-text-center tbk-text-blue-grey">
            You have no past sessions
          </div>
        )}
      </div>

      {hasMorePast && !isFetchingMorePast && (
        <div className="tbk-relative tbk-mb-7 tbk-mt-5 tbk-text-center">
          <button
            className="tbk-inline-block tbk-bg-grey-light tbk-px-[19px] tbk-py-0"
            onClick={() => loadMorePast()}
          >
            <div className="tbk-flex tbk-text-blue">
              Show More Past
              <CaretDownIcon />
            </div>
          </button>
          <hr className="tbk-mx-auto tbk--mt-1.5 tbk-mb-0 tbk-border-blue-grey-light" />
        </div>
      )}

      {isFetchingMorePast && (
        <div className="tbk-mb-5">{renderSkeletons(1, "booking")}</div>
      )}
    </>
  );

  const renderGroupClassesTab = () => {
    return (
      <>
        {/* Upcoming Group Classes */}
        <div className="tbk-mb-5">
          <div className="tbk-text-h3-subtitle tbk-mb-2 tbk-mt-4 tbk-text-blue-grey md:tbk-mb-2 md:tbk-mt-4">
            Upcoming:
          </div>
          <div className="tbk-text-small tbk-mb-3 tbk-hidden tbk-text-blue-grey md:tbk-mb-4">
            Time set to{" "}
            <TimeZoneLabel
              region={user ? user.region : process.env.REACT_APP_REGION}
            />
          </div>

          {upcomingEventsLoading ? (
            renderSkeletons(2, "event")
          ) : upcomingEvents && upcomingEvents.length > 0 ? (
            <>
              {upcomingEvents.map((event) => (
                <EventTile
                  key={event.id}
                  className="tbk-mb-2 tbk-bg-basic-white md:tbk-max-h-[96px]"
                  event={event}
                />
              ))}

              {hasMoreUpcomingEvents && !isFetchingMoreUpcomingEvents && (
                <div className="tbk-relative tbk-mb-7 tbk-mt-5 tbk-text-center">
                  <button
                    className="tbk-inline-block tbk-bg-grey-light tbk-px-[19px] tbk-py-0"
                    onClick={() => loadMoreUpcomingEvents()}
                  >
                    <div className="tbk-flex tbk-text-blue">
                      Show More Upcoming
                      <CaretDownIcon />
                    </div>
                  </button>
                  <hr className="tbk-mx-auto tbk--mt-1.5 tbk-mb-0 tbk-border-blue-grey-light" />
                </div>
              )}

              {isFetchingMoreUpcomingEvents && (
                <div className="tbk-mb-5">{renderSkeletons(1, "event")}</div>
              )}
            </>
          ) : (
            <div className="tbk-text-main tbk-mb-7 tbk-mt-7 tbk-text-center tbk-text-blue-grey">
              <div className="tbk-mb-4">You have no upcoming group classes</div>
              <Link className="tbk-inline-block" to={`/events/upcoming`}>
                <Button
                  variant="secondary"
                  className="tbk-pl-5 tbk-pr-5 lg:tbk-w-66"
                  endIcon={<ArrowRightIcon />}
                >
                  View Classes
                </Button>
              </Link>
            </div>
          )}
        </div>

        <hr />

        {/* Past Group Classes */}
        <div>
          <div className="tbk-text-h3-subtitle tbk-mb-2 tbk-mt-5 tbk-text-blue-grey">
            Past:
          </div>

          {pastEventsLoading ? (
            renderSkeletons(3, "event")
          ) : pastEvents && pastEvents.length > 0 ? (
            <>
              {pastEvents.map((event) => (
                <EventTile
                  key={event.id}
                  className="tbk-mb-2 tbk-bg-basic-white md:tbk-max-h-[96px]"
                  event={event}
                  isPastEvent={true}
                  // onClick={() => history.push(`/events/${event.id}`)}
                />
              ))}

              {hasMorePastEvents && !isFetchingMorePastEvents && (
                <div className="tbk-relative tbk-mb-7 tbk-mt-5 tbk-text-center">
                  <button
                    className="tbk-inline-block tbk-bg-grey-light tbk-px-[19px] tbk-py-0"
                    onClick={() => loadMorePastEvents()}
                  >
                    <div className="tbk-flex tbk-text-blue">
                      Show More Past
                      <CaretDownIcon />
                    </div>
                  </button>
                  <hr className="tbk-mx-auto tbk--mt-1.5 tbk-mb-0 tbk-border-blue-grey-light" />
                </div>
              )}

              {isFetchingMorePastEvents && (
                <div className="tbk-mb-5">{renderSkeletons(1, "event")}</div>
              )}
            </>
          ) : (
            <div className="tbk-text-main tbk-mt-1 tbk-text-center tbk-text-blue-grey">
              You have no past group classes
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <ScrollToTopOnMount />
      <div>
        {/* Tab Navigation */}
        <div className="tbk--mt-2 tbk-border-blue-grey-light">
          <div className="tbk-flex">
            <TabButton
              className="tbk-rounded-bl-lg tbk-border-l tbk-border-blue-grey-light"
              active={activeTab === TABS.GROUP_CLASSES}
              onClick={() => setActiveTab(TABS.GROUP_CLASSES)}
            >
              {TABS.GROUP_CLASSES}
            </TabButton>
            <TabButton
              className="tbk-rounded-br-lg tbk-border-l tbk-border-r tbk-border-blue-grey-light"
              active={activeTab === TABS.ONE_ON_ONE}
              onClick={() => setActiveTab(TABS.ONE_ON_ONE)}
            >
              {TABS.ONE_ON_ONE}
            </TabButton>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === TABS.ONE_ON_ONE && renderOneOnOneTab()}
        {activeTab === TABS.GROUP_CLASSES && renderGroupClassesTab()}
      </div>
    </>
  );
};

export default Bookings;
